const DAY_MS = 24 * 60 * 60 * 1000;

function safeInteger(value) {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : 0;
}

function normalizedDate(value) {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return "";
  const parsed = new Date(`${raw}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === raw
    ? raw
    : "";
}

function dateOrdinal(value) {
  const date = normalizedDate(value);
  return date ? Math.trunc(Date.parse(`${date}T00:00:00.000Z`) / DAY_MS) : null;
}

export function normalizeBriefStreakState(value, maximumActivityDate = "") {
  const raw = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const lastDate = normalizedDate(raw.lastDate);
  const maximum = normalizedDate(maximumActivityDate);
  const acceptedDate = lastDate && (!maximum || lastDate <= maximum) ? lastDate : "";
  const count = acceptedDate ? safeInteger(raw.count) : 0;
  return {
    count,
    best: acceptedDate ? Math.max(count, safeInteger(raw.best)) : 0,
    lastDate: acceptedDate,
    updatedAtEpochMs: safeInteger(raw.updatedAtEpochMs),
  };
}

function nextStreakClock(currentEpochMs, nowEpochMs) {
  const now = Math.max(1, safeInteger(nowEpochMs) || Date.now());
  const current = safeInteger(currentEpochMs);
  return Math.max(now, current + 1);
}

export function creditBriefStreakCompletion(state, todayDate, nowEpochMs = Date.now()) {
  const today = normalizedDate(todayDate);
  if (!today) return normalizeBriefStreakState(state);
  const current = normalizeBriefStreakState(state);
  if (current.lastDate > today) return current;
  if (current.lastDate === today) return current;

  const todayOrdinal = dateOrdinal(today);
  const priorOrdinal = dateOrdinal(current.lastDate);
  const count =
    priorOrdinal != null && todayOrdinal != null && todayOrdinal - priorOrdinal === 1
      ? Math.max(1, current.count) + 1
      : 1;
  return {
    count,
    best: Math.max(current.best, count),
    lastDate: today,
    updatedAtEpochMs: nextStreakClock(current.updatedAtEpochMs, nowEpochMs),
  };
}

export function maximumAcceptedBriefStreakDate(serverTime) {
  const boundary = new Date(serverTime);
  if (!Number.isFinite(boundary.getTime())) return "";
  boundary.setUTCDate(boundary.getUTCDate() + 1);
  return boundary.toISOString().slice(0, 10);
}

export function mergeBriefStreakStates(
  localState,
  remoteState,
  {
    remoteTimestampProvided = false,
    preferRemote = false,
    maximumActivityDate = "",
  } = {},
) {
  const local = normalizeBriefStreakState(localState, maximumActivityDate);
  const remote = normalizeBriefStreakState(remoteState, maximumActivityDate);
  if (!remote.lastDate) return local;
  const clockAccepted = remoteTimestampProvided
    ? remote.updatedAtEpochMs > 0 &&
      (preferRemote ||
        local.updatedAtEpochMs === 0 ||
        remote.updatedAtEpochMs >= local.updatedAtEpochMs)
    : preferRemote || local.updatedAtEpochMs === 0;

  let count = local.count;
  let lastDate = local.lastDate;
  if (local.lastDate && local.lastDate === remote.lastDate) {
    count = Math.max(local.count, remote.count);
    lastDate = remote.lastDate;
  } else if (clockAccepted && (!local.lastDate || remote.lastDate > local.lastDate)) {
    count = remote.count;
    lastDate = remote.lastDate;
  }
  const best = Math.max(local.best, remote.best, lastDate ? count : 0);
  const resolvedDiffersFromRemote =
    count !== remote.count || lastDate !== remote.lastDate || best !== remote.best;
  const remoteClock = remoteTimestampProvided ? remote.updatedAtEpochMs : 0;
  const mustRepublishLocalWinner =
    remoteClock > 0 &&
    remoteClock >= local.updatedAtEpochMs &&
    resolvedDiffersFromRemote;
  const updatedAtEpochMs = mustRepublishLocalWinner
    ? nextStreakClock(Math.max(local.updatedAtEpochMs, remoteClock), Date.now())
    : Math.max(local.updatedAtEpochMs, remoteClock);
  return {
    count,
    best,
    lastDate,
    updatedAtEpochMs,
  };
}
